import React, { useCallback, useState } from "react"

import {
	Grid,
	Tooltip,
	Typography
} from "@material-ui/core"
import {
	InfoOutlined as InfoIcon
} from "@material-ui/icons"

import useRDStationCRMApi from "@/hooks/useRDStationCRMApi"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"
import { Notification } from "@/components"

import RDStationCRMService from "@/services/RDStationCRM"

import {
	getCurrentRDStationDealUpdate
} from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import { RDStationCRMPipeline, RDStationCRMStage } from "@/protocols/RdStationPluginProtocol"
import { DeepPartial } from "@/protocols/utility"
import { ChatBotFlowBlockContent, RDStationDealUpdate } from "@/protocols/chatBot"

import { colors } from "@/styles/colors"

type DealUpdateOptionsSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const DealUpdateOptionsSectionEditor: React.FC<DealUpdateOptionsSectionEditorProps> = (props) => {
	const {
		onSave,
		chatBotFlowBlockContent,
		children
	} = props

	const {
		getPipelineAndStageTitle,
		validatePlugin,
		DEAL_STATUS_OPTIONS
	} = useRDStationCRMApi()

	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const currentDealUpdate = getCurrentRDStationDealUpdate(contentEditor.content)

	const [pipelines, setPipelines] = useState<RDStationCRMPipeline[]>([])

	const getPipelinesRDStation = useCallback(async () => {
		const isValidPlugin = await validatePlugin()

		if (isValidPlugin) {
			try {
				const responseData = await RDStationCRMService.getPipelines()
				setPipelines(responseData)
			} catch (error) {
				Notification.error({ message: "Não foi possível carregar os funis de vendas." })
			}
		}
	}, [pipelines])

	const {
		pipelineTitle,
		stageTitle
	} = getPipelineAndStageTitle({
		pipeline: currentDealUpdate?.pipeline,
		stage: currentDealUpdate?.stage
	})

	const currentStatusOption = DEAL_STATUS_OPTIONS.find(option => option.slug === currentDealUpdate?.status)

	const handleOpen = () => {
		contentEditor.reset()
	}

	const handleSave = () => {
		onSave(contentEditor.content)
	}

	const handleFilterPipelineStages = (groupTitle: string): RDStationCRMStage[] => {
		if (groupTitle === "") {
			return []
		}

		const selectedPipeline = pipelines?.find((dealGroup) => dealGroup.title === groupTitle)

		return selectedPipeline?.stages || []
	}

	const handleChangeDealUpdateContent = (dealUpdate: DeepPartial<RDStationDealUpdate>) => {
		contentEditor.changeContent({
			rdStationDealUpdate: dealUpdate
		})
	}

	return (
		<BlockBodySectionEditor
			title="SELECIONE UM FUNIL DE VENDAS, UMA ETAPA E UM STATUS PARA ATUALIZAR O NEGÓCIO"
			onSave={handleSave}
			onOpen={handleOpen}
			AsideDrawerProps={{
				children: (
					<Grid
						container
						direction="row"
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								direction="column"
								spacing={1}
							>
								<Grid
									item
								>
									<Grid
										container
										alignItems="center"
										spacing={1}
									>
										<Grid
											item
										>
											<Typography variant="h4">Funil de vendas</Typography>
										</Grid>
										<Grid
											item
										>
											<Grid
												container
												alignItems="center"
											>
												<Tooltip title="Serão exibidos apenas os funis de vendas retornados pela API do RD Station CRM.">
													<InfoIcon
														htmlColor={colors.gray[400]}
														style={{
															width: 16,
															height: 16
														}}
													/>
												</Tooltip>
											</Grid>
										</Grid>
									</Grid>
								</Grid>

								<Grid
									item
								>
									<SelectInput
										defaultOption={{
											title: pipelineTitle
										}}
										onChange={option => {
											handleChangeDealUpdateContent({
												pipeline: {
													id: option.id,
													title: option.title
												},
												stage: {
													title: ""
												}
											})
										}}
										options={pipelines}
										placeholder="Selecione um Funil de Vendas"
										getOptionSelected={(option, value) => option.title === value.title}
										getOptionLabel={(option) => option.title}
										onUpdate={getPipelinesRDStation}
									/>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid container direction="column" spacing={1}>
								<Grid item>
									<Typography variant="h4">Etapa</Typography>
								</Grid>
								<Grid item>
									<SelectInput
										value={{
											title: stageTitle
										}}
										onChange={option => (
											handleChangeDealUpdateContent({
												stage: {
													...option
												}
											})
										)}
										options={handleFilterPipelineStages(pipelineTitle)}
										placeholder="Selecione uma Etapa"
										getOptionSelected={(option, value) => option.title === value.title}
										getOptionLabel={(option) => option.title}
									/>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid container direction="column" spacing={1}>
								<Grid item>
									<Typography variant="h4">Status</Typography>
								</Grid>
								<Grid item>
									<SelectInput
										defaultOption={{
											name: currentStatusOption?.name || ""
										}}
										onChange={option => (
											handleChangeDealUpdateContent({
												status: option?.slug
											})
										)}
										options={DEAL_STATUS_OPTIONS}
										placeholder="Selecione um Status"
										getOptionSelected={(option, value) => option.slug === value.slug}
										getOptionLabel={(option) => option.name}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default DealUpdateOptionsSectionEditor
